<template>
  <aw-spring :desktop-size="10" :class="$style.spring">
    <aw-breadcrumb-version2 :class="$style.breadcrumb" />
    <aw-cms-white-card :class="$style.card">
      <template #head>
        <lv-heading
          level="1"
          tag="h1"
          version2
        >
          {{ $awt('aw.pages.petrol.title') }}
        </lv-heading>
      </template>
      <div :class="$style.petrolPage">
        <aw-petrol-menu :petrol-station="selectedPetrolStation" :class="$style.petrolMenu">
          <div v-if="selectedPetrolStation" :class="$style.petrolContainer">
            <!-- Petrol data -->
            <aw-section-wrapper :has-padding="false" :class="$style.petrolDataWrapper">
              <template #content="">
                <div :class="$style.petrolData">
                  <div :class="$style.petrolDataFirstRow">
                    <lv-heading
                      level="2"
                      tag="h2"
                      version2
                      :class="$style.petrolDataTitle"
                    >
                      {{ $awt('aw.pages.petrol.list.title') }}
                    </lv-heading>
                    <aw-select-version2
                      v-model:selected-options="actualPetrolStation"
                      :options="petrolStations"
                      :label="$awt('aw.pages.petrol.list.title')"
                      :multiple="false"
                      :class="$style.petrolListSelect"
                      :dropdown-icon-attrs="{
                        name: 'arrow-down-16',
                        size: 20
                      }"
                      @update:selected-options="selectPetrol"
                    />
                  </div>

                  <aw-store-map
                    v-model="mapBounds"
                    :type="TYPE_OSM"
                    :has-tooltip="false"
                    :class="$style.petrolDataMap"
                    :selected-store-id="selectedPetrolStation.id"
                  />
                  <div :class="$style.petrolDataPrices">
                    <lv-heading
                      level="4"
                      tag="h4"
                      version2
                      :class="$style.petrolDataPricesTitle"
                    >
                      {{ $awt('aw.pages.petrol.prices.title') }}
                    </lv-heading>
                    <div :class="$style.petrolDataPricesContainer">
                      <div
                        v-for="(item, i) in selectedPetrolStation.prices"
                        :key="`petrol-price-${i}`"
                        :class="$style.petrolDataPricesRow"
                      >
                        <span :class="[$style.petrolDataPricesType, petrolDataPricesTypeColor(item.petrolToken)]">{{ $awt(`aw.pages.petrol.prices.${item.petrolToken}`) }}</span>
                        <span :class="$style.petrolDataPricesPrice">{{ $price({ gross: item.price.grossDiscounted }, item.price.currency) }}</span>
                      </div>
                    </div>
                  </div>
                  <div id="address" :class="[$style.petrolDataAddress, $style.petrolDataInfoBlock]">
                    <span :class="$style.petrolDataIconWrapper">
                      <lv-icon name="location-16" :size="24" />
                    </span>
                    <div :class="$style.petrolDataSectionContent">
                      <div :class="$style.petrolDataSectionTitle">
                        {{ $awt('aw.pages.petrol.address.title') }}
                      </div>
                      <span :class="$style.petrolDataSectionContentAddress">{{
                        selectedPetrolStation?.address ? formatAddress(selectedPetrolStation?.address) : ''
                      }}</span>
                      <lv-link
                        :to="selectedPetrolStation.googleMapsLink ?? ''"
                        :class="$style.petrolDataMapLink"
                        target="_blank"
                      >
                        {{ $awt('aw.pages.petrol.address.navigation_to_petrol_station') }}
                      </lv-link>
                    </div>
                  </div>
                  <div id="openingHours" :class="[$style.petrolDataOpeningHours, $style.petrolDataInfoBlock]">
                    <span :class="$style.petrolDataIconWrapper">
                      <lv-icon name="time-clock-circle-25" :size="24" />
                    </span>
                    <div :class="$style.petrolDataSectionContent">
                      <div :class="$style.petrolDataSectionTitle">
                        {{ $awt('aw.pages.petrol.opening_hours.title') }}
                      </div>
                      <div :class="$style.petrolDataOpeningHoursWrapper">
                        <span
                          v-for="(openingHour, i) in selectedPetrolStation.openingHours"
                          :key="i"
                          :class="$style.petrolDataOpeningHoursValue"
                        >{{ openingHour.key }}: {{ openingHour.value }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </aw-section-wrapper>
            <!-- Petrol data -->
            <aw-separator margin-bottom="md" margin-top="md" />
            <!-- Services -->
            <div :class="[{[$style.swipeable]: selectedPetrolStation.services.length}]">
              <aw-services-list id="services" :services="selectedPetrolStation.services" />
            </div>
            <!-- Services -->
            <!-- Introduction -->
            <aw-section-wrapper
              :has-left-pedding="false"
              :section-title="$awt('aw.pages.petrol.introduction.title')"
              :class="$style.petrolDescriptionWrapper"
            >
              <template #content="">
                <div :class="$style.petrolDescription" v-html="$awt('aw.pages.petrol.introduction.description')" />
              </template>
            </aw-section-wrapper>
            <!-- Introduction -->
            <!-- Actual news -->
            <div :class="[$style.newsWrapper, {[$style.swipeable]: !isNewsListEmpty}]">
              <aw-news
                id="news"
                :store="selectedPetrolStation"
                :breakpoints-overwrite="shortlistBreakpoints"
                page-type="petrol"
                :class="$style.news"
                @is-empty="handleNewsListEmpty"
              />
            </div>
            <!-- Actual news -->
          </div>
        </aw-petrol-menu>
      </div>
    </aw-cms-white-card>
  </aw-spring>
</template>

<script>
  import { defineNuxtComponent, navigateTo } from 'nuxt/app';
  import { mapState } from 'pinia';
  import { defineAsyncComponent, provide } from 'vue';
  import AwCmsWhiteCard from '~~/common/components/AwCmsWhiteCard';
  import { LvHeading, LvIcon } from '~~/common/components/loginet-vue-shop/index';
  import AwSeparator from '~~/common/components/Common/AwSeparator.vue';
  import { useUserInterfaceStore } from '~~/common/stores/userInterface';
  import { useBreadcrumbStore } from '~~/common/stores/breadcrumb.js';
  import AwBreadcrumbVersion2 from '~~/common/components/Common/AwBreadcrumbVersion2';
  import AwSpring from '~~/common/components/AwSpring';
  import LvLink from '~~/common/components/loginet-vue-shop/components/LvLink.vue';
  import addressFormatter from '~~/common/mixins/addressFormatter.js';
  import AwStoreMap, { TYPE_OSM } from '~~/shop/app/components/Common/Map/AwStoreMap';
  import { usePetrolBreadcrumbs } from '~~/common/composables/usePetrolBreadcrumbs';

  export default defineNuxtComponent({
    name: 'AwPetrol',
    components: {
      AwPetrolMenu: defineAsyncComponent(() => import('~~/shop/app/components/Page/Petrol/AwPetrolMenu')),
      AwStoreMap,
      AwServicesList: defineAsyncComponent(() => import('~~/shop/app/components/Common/Store/AwServicesList')),
      AwNews: defineAsyncComponent(() => import('~~/common/components/AwNews')),
      AwSectionWrapper: defineAsyncComponent(() => import('~~/common/components/Page/Common/AwSectionWrapper')),
      LvLink,
      AwCmsWhiteCard,
      LvHeading,
      AwSeparator,
      AwSelectVersion2: defineAsyncComponent(() => import('~~/common/components/Common/AwSelectVersion2')),
      AwBreadcrumbVersion2,
      AwSpring,
      LvIcon,
    },
    mixins: [addressFormatter],
    props: {
      petrolStations: {
        type: Array,
        require: true,
        default: () => ([]),
      },
      selectedPetrolStation: {
        type: Object,
        require: true,
        default: () => ({}),
      },
    },
    setup () {
      provide('getRowIndexById', () => 0);
      return usePetrolBreadcrumbs();
    },
    data () {
      return {
        TYPE_OSM,
        actualPetrolStation: {},
        menuItems: [],
        isNewsListEmpty: false,
      };
    },
    computed: {
      ...mapState(useUserInterfaceStore, {
        screenRange: state => state.mediaQueries,
      }),
      shortlistBreakpoints () {
        return [
          {
            at: this.screenRange['desktop-large-min'],
            count: 4,
          },
          {
            at: this.screenRange['desktop-small-min'],
            count: 3,
          },
          {
            at: this.screenRange['tablet-min'],
            count: 2,
          },
          {
            at: true,
            count: 'auto',
          },
        ];
      },
      mapBounds: {
        get () {
          return this.selectedPetrolStation.address.coordinates ?
            [
              this.transformCoordinates(this.selectedPetrolStation.address.coordinates),
              this.transformCoordinates(this.selectedPetrolStation.address.coordinates),
            ]
            : this.$awRuntimeConfig.mapAreaBounds;
        },
        set () {
        },
      },
    },
    mounted () {
      this.actualPetrolStation = this.selectedPetrolStation;
      const breadcrumbStore = useBreadcrumbStore();

      breadcrumbStore.setBreadcrumbs([
        {
          token: this.$awt('aw.common.category.home'),
          to: '/',
          disabled: false,
        },
        this.petrolHomeBreadcrumb(false),
        {
          token: this.selectedPetrolStation?.name,
          to: {
            name: 'petrol-slug',
            params: { slug: this.selectedPetrolStation?.slug },
          },
          disabled: true,
        },
      ]);
    },
    methods: {
      selectPetrol (newPetrol) {
        navigateTo(this.$localePath({
          name: 'petrol-slug',
          params: { slug: newPetrol.slug },
        }));
      },
      petrolDataPricesTypeColor (priceKey) {
        switch (true) {
          case priceKey.startsWith('petrol_95'):
            return this.$style.petrolDataPricesTypeColorPetrol;
          case priceKey.startsWith('petrol_pro_100'):
            return this.$style.petrolDataPricesTypeColorSuper;
          case priceKey.includes('diesel'):
            return this.$style.petrolDataPricesTypeColorDiesel;
          case priceKey.startsWith('pb_gas_23'):
          case priceKey.startsWith('pg_gas_11'):
          default:
            return this.$style.petrolDataPricesTypeColorPB;
        }
      },
      transformCoordinates (coordinates) {
        return {
          latitude: parseFloat(coordinates.latitude),
          longitude: parseFloat(coordinates.longitude),
        };
      },
      handleNewsListEmpty (newData) {
        this.isNewsListEmpty = newData;
      },
    },
  });
</script>

<style module lang="scss" rel="stylesheet/scss">
@use "sass:color";

.spring {
  @include mobile(max) {
    max-width: 100%;
    margin: 0;
  }
}

.breadcrumb {
  margin-bottom: 20px;
  @include tablet(min) {
    margin-bottom: 40px;
  }

  @include mobile(max) {
    padding: 2px 16px;
  }
}

.card {
  padding: 20px 16px;

  @include tablet(min) {
    padding: 40px 24px;
  }

  @include desktop-small(min) {
    padding: 40px;
  }
}

.petrol {
  --awTabHeadingLeft: 0 !important;

  &Menu {
    @include desktop-medium(min) {
      grid-column: span 2;
    }
  }

  &Container {
    display: flex;
    flex-direction: column;

    @include desktop-medium(min) {
      grid-column: span 8;
    }
  }

  &ListSelect {
    [data-form-input-select] {
      height: 52px;
    }

    @include desktop-medium(min) {
      max-width: 278px;
      justify-self: flex-end;
    }
  }

  &Data {
    display: grid;
    padding: 20px 16px;
    border-radius: 16px;
    background-color: $color-background-3;
    grid-template-areas: "title" "map" "prices" "address" "openingHours";
    grid-template-columns: 1fr;
    gap: 20px;

    @include tablet(min) {
      grid-template-areas: "title title"  "map prices" "address openingHours";
      grid-template-columns: repeat(2, 1fr);
      gap: 40px 24px;
      padding: 24px;
    }

    @include desktop-small(min) {
      grid-template-areas: "title title" "map prices" "address openingHours";
      grid-template-columns: repeat(2, 1fr);
      gap: 40px 32px;
      padding: 32px;
      border-radius: 24px;
    }

    @include desktop-medium(min) {
      padding: 40px;
    }

    &FirstRow {
      grid-area: title;
      display: grid;
      grid-template-columns: 1fr;
      gap: 20px;

      @include desktop-small(min) {
        gap: 32px;
        grid-template-columns: repeat(2, 1fr);
      }

      @include desktop-medium(min) {
        gap: 40px;
      }
    }

    &Title {
      align-self: center;
      @include tablet(min) {
        margin-top: 16px !important;
        margin-bottom: 16px !important;
      }
      @include desktop-small(min) {
        margin-top: unset !important;
        margin-bottom: unset !important;
      }
    }

    &IconWrapper {
      grid-area: icon;
      display: flex;
      width: 56px;
      height: 56px;
      padding: 16px;
      border-radius: 16px;
      background-color: $color-background-1;
    }

    &SectionTitle {
      font-size: 14px;
      font-weight: $font-weight-bold-v2;
      line-height: 20px;

      @include tablet(min) {
        font-size: 16px;
        line-height: 24px;
      }
    }

    &SectionContent {
      grid-area: content;
      display: flex;
      flex-direction: column;

      &Address {
        font-size: 12px;
        font-weight: $font-weight-regular-v2;
        line-height: 16px;

        @include tablet(min) {
          font-size: 14px;
          line-height: 20px;
        }
      }
    }

    &InfoBlock {
      display: grid;
      grid-template-areas: "icon content";
      gap: 20px;
      grid-template-columns: max-content;

      @include desktop-small(min) {
        grid-template-areas: "icon" "content";
        padding: 0;
        grid-template-columns: 1fr;
        grid-template-rows: max-content;
      }

      @include desktop-medium(min) {
        grid-template-areas: "icon content";
        padding: 0;
        grid-template-columns: max-content;
      }

      .petrolDataSectionContent {
        gap: 8px;
      }
    }

    &Address {
      padding: 0 0 20px;
      border-bottom: 1px dashed $color-dash-separator;
      grid-area: address;


      @include tablet(min) {
        padding: 0 24px 0 0;
        border-right: 1px dashed $color-dash-separator;
        border-bottom: 0;
      }

      @include desktop-small(min) {
        padding-right: 32px;
      }

      @include desktop-medium(min) {
        padding-right: 40px;
      }
    }

    &OpeningHours {
      grid-area: openingHours;


      .petrolDataSectionContent {
        gap: 0;

        .petrolDataSectionTitle {
          margin-bottom: 8px;
        }
      }

      &Wrapper {
        display: grid;
        grid-template-columns: max-content;
      }

      &Value {
        font-size: 12px;
        font-weight: $font-weight-regular-v2;
        line-height: 16px;

        @include tablet(min) {
          font-size: 14px;
          font-weight: $font-weight-normal-v2;
          line-height: 20px;
        }
      }
    }

    &Map {
      z-index: 0;
      width: 100%;
      min-height: 244px;
      border-radius: 24px;
      grid-area: map;
      @include desktop-small(min) {
        min-height: 344px;
      }

      &Link {
        font-size: 12px;
        font-weight: $font-weight-medium-v2;
        line-height: 16px;
        cursor: pointer;

        @include tablet(min) {
          font-size: 14px;
          line-height: 20px;
        }
      }
    }

    &Prices {
      &Title {
        font-size: 12px !important;
        line-height: 16px !important;
        margin-bottom: 8px !important;

        @include desktop-small(min) {
          font-size: 14px !important;
          line-height: 20px !important;
          margin-bottom: 20px !important;
        }
      }

      &Container {
        display: flex;
        flex-direction: column;
        gap: 4px;
        @include desktop-small(min) {
          gap: 8px;
        }
      }

      &Row {

        font-size: 12px;
        font-weight: $font-weight-bold-v2;
        line-height: 16px;
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: space-between;
        padding: 4px 8px;
        border-radius: 12px;
        background-color: $color-background-1;

        @include desktop-small(min) {
          font-size: 14px;
          line-height: 20px;
          padding: 8px;
          border-radius: 16px;
        }
      }

      &Type {
        padding: 4px 8px;
        border-radius: 6px;

        @include desktop-small(min) {
          padding: 4px 12px;
          border-radius: 10px;
        }

        &Color {
          &Petrol {
            color: $color-text-sage;
            background-color: $color-sage-450--12;
          }

          &Super {
            color: $color-text-marigold;
            background-color: $color-marigold-300--12;
          }

          &Diesel {
            color: $color-olive-750;
            background-color: $color-olive-750--12;
          }

          &PB {
            color: $color-text-secondary;
            background-color: color.change($color-white, $alpha: 0.4);
          }
        }
      }
    }
  }

  &Description {
    font-size: 14px;
    font-weight: $font-weight-normal-v2;
    line-height: 20px;

    @include tablet(min) {
      font-size: 16px;
      line-height: 24px;
    }

    &Wrapper {
      @include tablet(min) {
        margin-top: 40px;
      }
    }
  }
}

.news {
  overflow: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &Wrapper {
    display: grid;
    width: auto;
    margin-top: 20px;
    @include tablet(min) {
      margin-top: 40px;
    }
  }
}

.swipeable {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  @include mobile(max) {
    right: -16px;
    width: calc(100% + 16px);
  }
}

</style>
